/**
 * PawLink Care Reminders — compact dashboard card.
 *
 * Shows Upcoming / Overdue / Completed for the signed-in user.
 * Quick-add form: pet · type · title · date+time · note · notify offsets.
 *
 * Free tier restriction is enforced server-side (POST /api/reminders
 * returns 402 for additional pets). We surface the server error rather
 * than duplicating the entitlement logic client-side.
 *
 * Compact by design — no medical taxonomy, no long forms, no calendars.
 */
import { useEffect, useMemo, useState } from "react";
import { PawPrint, Plus, CheckCircle, Trash, ClockCounterClockwise, WarningCircle } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const TYPES = [
    { value: "vaccination", labelKey: "care.type_vaccination" },
    { value: "vet_visit",   labelKey: "care.type_vet" },
    { value: "medication",  labelKey: "care.type_medication" },
    { value: "grooming",    labelKey: "care.type_grooming" },
    { value: "other",       labelKey: "care.type_other" },
];

const OFFSETS = ["7d", "1d", "0d"];

function _minDateTimeLocal() {
    // Right now, rounded to the next minute, formatted for datetime-local.
    const d = new Date();
    d.setSeconds(0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function CareRemindersCard({ pets = [], defaultPetId = null }) {
    const { t } = useI18n();
    const [groups, setGroups] = useState({ upcoming: [], overdue: [], completed: [] });
    const [tab, setTab] = useState("upcoming");
    const [showForm, setShowForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [form, setForm] = useState({
        pet_id: defaultPetId || pets[0]?.id || "",
        type: "vaccination",
        title: "",
        due_at: _minDateTimeLocal(),
        note: "",
        offsets: ["1d"],
    });

    const refresh = async () => {
        try {
            const { data } = await api.get("/reminders/mine");
            setGroups(data);
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail));
        }
    };

    useEffect(() => { refresh(); }, []);
    useEffect(() => {
        // Keep the form's pet_id in sync if the user's pet list arrives late.
        if (!form.pet_id && pets[0]?.id) setForm((f) => ({ ...f, pet_id: pets[0].id }));
    }, [pets, form.pet_id]);

    const toggleOffset = (o) => {
        setForm((f) => ({
            ...f,
            offsets: f.offsets.includes(o) ? f.offsets.filter((x) => x !== o) : [...f.offsets, o],
        }));
    };

    const save = async () => {
        if (saving) return;
        setSaving(true); setError("");
        try {
            const iso = new Date(form.due_at).toISOString();
            await api.post("/reminders", {
                pet_id: form.pet_id,
                type: form.type,
                title: form.title.trim(),
                due_at: iso,
                note: form.note.trim() || undefined,
                notify_offsets: form.offsets.length ? form.offsets : ["1d"],
            });
            setShowForm(false);
            setForm({ ...form, title: "", note: "" });
            refresh();
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || t("care.err_save"));
        } finally {
            setSaving(false);
        }
    };

    const complete = async (id) => {
        try {
            await api.patch(`/reminders/${id}`, { status: "completed" });
            refresh();
        } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    };
    const remove = async (id) => {
        try {
            await api.delete(`/reminders/${id}`);
            refresh();
        } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    };

    const items = groups[tab] || [];
    const petName = useMemo(() => (id) => pets.find((p) => p.id === id)?.name || "", [pets]);

    return (
        <section
            data-testid="care-card"
            className="rounded-3xl border border-paw-line dark:border-white/10 bg-white/95 dark:bg-white/[0.04] p-5 sm:p-6"
        >
            <div className="flex items-center justify-between gap-3 mb-4">
                <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-paw-terra">
                        {t("care.kicker")}
                    </p>
                    <h2 className="mt-1 font-display text-xl sm:text-2xl text-paw-ink dark:text-paw-bone">
                        {t("care.title")}
                    </h2>
                </div>
                <button
                    type="button"
                    onClick={() => setShowForm((s) => !s)}
                    data-testid="care-add-btn"
                    disabled={!pets.length}
                    className="inline-flex items-center gap-1.5 rounded-full bg-paw-terra hover:bg-paw-terraDark text-white px-4 h-9 text-sm font-semibold disabled:opacity-50"
                >
                    <Plus size={14} weight="bold" />
                    {t("care.add")}
                </button>
            </div>

            {!pets.length && (
                <p className="text-[13px] text-paw-ink2 dark:text-paw-bone/70 mb-3">
                    {t("care.empty_no_pets")}
                </p>
            )}

            <AnimatePresence>
                {showForm && pets.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25 }}
                        data-testid="care-add-form"
                        className="mb-4 rounded-2xl border border-paw-line dark:border-white/10 bg-paw-bone/50 dark:bg-white/[0.03] p-4 space-y-3 overflow-hidden"
                    >
                        <div className="grid grid-cols-2 gap-2">
                            <label className="text-[12px] text-paw-ink2 dark:text-paw-bone/70">
                                {t("care.pet")}
                                <select
                                    data-testid="care-form-pet"
                                    value={form.pet_id}
                                    onChange={(e) => setForm({ ...form, pet_id: e.target.value })}
                                    className="mt-1 w-full rounded-lg border border-paw-line dark:border-white/15 bg-white dark:bg-white/[0.05] text-paw-ink dark:text-paw-bone px-2 h-9 text-sm"
                                >
                                    {pets.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
                                </select>
                            </label>
                            <label className="text-[12px] text-paw-ink2 dark:text-paw-bone/70">
                                {t("care.type")}
                                <select
                                    data-testid="care-form-type"
                                    value={form.type}
                                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                                    className="mt-1 w-full rounded-lg border border-paw-line dark:border-white/15 bg-white dark:bg-white/[0.05] text-paw-ink dark:text-paw-bone px-2 h-9 text-sm"
                                >
                                    {TYPES.map((tp) => (
                                        <option key={tp.value} value={tp.value}>{t(tp.labelKey)}</option>
                                    ))}
                                </select>
                            </label>
                        </div>
                        <label className="block text-[12px] text-paw-ink2 dark:text-paw-bone/70">
                            {t("care.title_label")}
                            <input
                                data-testid="care-form-title"
                                value={form.title}
                                onChange={(e) => setForm({ ...form, title: e.target.value })}
                                placeholder={t("care.title_placeholder")}
                                className="mt-1 w-full rounded-lg border border-paw-line dark:border-white/15 bg-white dark:bg-white/[0.05] text-paw-ink dark:text-paw-bone px-3 h-9 text-sm"
                                required
                            />
                        </label>
                        <label className="block text-[12px] text-paw-ink2 dark:text-paw-bone/70">
                            {t("care.when")}
                            <input
                                type="datetime-local"
                                data-testid="care-form-due"
                                value={form.due_at}
                                min={_minDateTimeLocal()}
                                onChange={(e) => setForm({ ...form, due_at: e.target.value })}
                                className="mt-1 w-full rounded-lg border border-paw-line dark:border-white/15 bg-white dark:bg-white/[0.05] text-paw-ink dark:text-paw-bone px-3 h-9 text-sm"
                                required
                            />
                        </label>
                        <label className="block text-[12px] text-paw-ink2 dark:text-paw-bone/70">
                            {t("care.note")}
                            <textarea
                                data-testid="care-form-note"
                                value={form.note}
                                onChange={(e) => setForm({ ...form, note: e.target.value })}
                                rows={2}
                                className="mt-1 w-full rounded-lg border border-paw-line dark:border-white/15 bg-white dark:bg-white/[0.05] text-paw-ink dark:text-paw-bone px-3 py-1.5 text-sm resize-none"
                            />
                        </label>
                        <div>
                            <p className="text-[12px] text-paw-ink2 dark:text-paw-bone/70">{t("care.notify_me")}</p>
                            <div className="mt-1.5 flex flex-wrap gap-2">
                                {OFFSETS.map((o) => (
                                    <button
                                        key={o}
                                        type="button"
                                        onClick={() => toggleOffset(o)}
                                        data-testid={`care-form-offset-${o}`}
                                        aria-pressed={form.offsets.includes(o)}
                                        className={
                                            "rounded-full px-3 h-7 text-[12px] font-medium border transition " +
                                            (form.offsets.includes(o)
                                                ? "bg-paw-terra text-white border-paw-terra"
                                                : "border-paw-line dark:border-white/15 text-paw-ink dark:text-paw-bone/85 hover:bg-paw-terra/10")
                                        }
                                    >
                                        {t(`care.offset_${o}`)}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {error && <p data-testid="care-form-error" className="text-[12px] text-red-600 dark:text-red-400">{error}</p>}
                        <div className="flex justify-end gap-2 pt-1">
                            <button
                                type="button"
                                onClick={() => setShowForm(false)}
                                className="rounded-full px-3 h-9 text-sm text-paw-ink dark:text-paw-bone/85 border border-paw-line dark:border-white/15"
                            >
                                {t("care.cancel")}
                            </button>
                            <button
                                type="button"
                                onClick={save}
                                data-testid="care-form-save"
                                disabled={!form.title.trim() || !form.pet_id || saving}
                                className="rounded-full bg-paw-terra hover:bg-paw-terraDark text-white px-4 h-9 text-sm font-semibold disabled:opacity-60"
                            >
                                {saving ? t("care.saving") : t("care.save")}
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Tabs */}
            <div className="mb-3 flex items-center gap-1 rounded-full bg-paw-bone/60 dark:bg-white/[0.03] p-1 border border-paw-line dark:border-white/10 w-fit">
                {["upcoming", "overdue", "completed"].map((k) => (
                    <button
                        key={k}
                        type="button"
                        onClick={() => setTab(k)}
                        data-testid={`care-tab-${k}`}
                        className={
                            "rounded-full px-3 h-7 text-[12px] font-semibold transition " +
                            (tab === k ? "bg-paw-terra text-white" : "text-paw-ink dark:text-paw-bone/80")
                        }
                    >
                        {t(`care.tab_${k}`)} ({(groups[k] || []).length})
                    </button>
                ))}
            </div>

            {items.length === 0 ? (
                <p data-testid="care-empty" className="text-[13px] text-paw-ink2 dark:text-paw-bone/60 py-4 text-center">
                    {t(`care.empty_${tab}`)}
                </p>
            ) : (
                <ul className="space-y-2" data-testid="care-list">
                    {items.map((r) => {
                        const when = new Date(r.due_at);
                        return (
                            <li key={r.id} data-testid={`care-item-${r.id}`} className="flex items-start gap-3 rounded-xl border border-paw-line dark:border-white/10 bg-white/60 dark:bg-white/[0.03] px-3 py-2.5">
                                <span className={`mt-0.5 flex-shrink-0 w-8 h-8 rounded-full grid place-items-center ${
                                    tab === "overdue"
                                        ? "bg-red-500/15 text-red-600"
                                        : tab === "completed"
                                        ? "bg-emerald-500/15 text-emerald-700"
                                        : "bg-paw-terra/15 text-paw-terra"
                                }`}>
                                    {tab === "overdue" ? <WarningCircle size={16} weight="fill" />
                                        : tab === "completed" ? <CheckCircle size={16} weight="fill" />
                                        : <ClockCounterClockwise size={16} weight="fill" />}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <p className="text-[14px] font-semibold text-paw-ink dark:text-paw-bone leading-snug truncate">{r.title}</p>
                                    <p className="text-[12px] text-paw-ink2 dark:text-paw-bone/70 leading-tight">
                                        {petName(r.pet_id)} · {t(`care.type_${r.type === "vet_visit" ? "vet" : r.type}`)} · <span dir="ltr">{when.toLocaleString()}</span>
                                    </p>
                                    {r.note && <p className="mt-1 text-[12px] text-paw-ink2 dark:text-paw-bone/60 line-clamp-2">{r.note}</p>}
                                </div>
                                {tab !== "completed" && (
                                    <button
                                        type="button"
                                        onClick={() => complete(r.id)}
                                        data-testid={`care-complete-${r.id}`}
                                        title={t("care.mark_done")}
                                        className="w-8 h-8 rounded-full grid place-items-center text-emerald-700 hover:bg-emerald-500/10"
                                    >
                                        <CheckCircle size={16} weight="fill" />
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => remove(r.id)}
                                    data-testid={`care-delete-${r.id}`}
                                    title={t("care.delete")}
                                    className="w-8 h-8 rounded-full grid place-items-center text-red-600 hover:bg-red-500/10"
                                >
                                    <Trash size={15} />
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
}
